import React, { useMemo, useImperativeHandle, forwardRef } from 'react';
import ReportLayout from '../core/ReportLayout';
import { formatCurrency } from '../shared/utils';
import { exportToPDF, exportToExcel } from '../shared/exportUtils';
import { FileText } from 'lucide-react';
import { successToast, errorToast } from '../../../lib/helpers';

const WeeklySales = forwardRef(function WeeklySales({ data, isLoading, searchTerm, hideHeader = false }, ref) {
    const filteredData = useMemo(() => {
        if (!data?.data) return [];

        let filtered = data.data;
        if (searchTerm) {
            const lowercaseSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(item =>
                item?.name?.toLowerCase().includes(lowercaseSearch) ||
                item?.area?.toLowerCase().includes(lowercaseSearch)
            );
        }
        return filtered;
    }, [data, searchTerm]);

    const totals = useMemo(() => {
        if (!filteredData.length || !data?.columnDates) return null;

        try {
            const totalsByDay = data.columnDates.reduce((acc, date) => {
                acc[date] = filteredData.reduce((sum, item) => {
                    const dailySale = item?.dailySales?.[date] || 0;
                    return sum + dailySale;
                }, 0);
                return acc;
            }, {});

            const total = filteredData.reduce((sum, item) => sum + (item?.totalValue || 0), 0);

            return {
                daily: totalsByDay,
                total: total
            };
        } catch (error) {
            console.error('Error calculating totals:', error);
            return null;
        }
    }, [filteredData, data?.columnDates]);

    const prepareExportData = () => {
        const tableData = filteredData.map(item => ({
            name: item.name || 'N/A',
            area: item.area || 'N/A',
            total: item.totalValue || 0,
            ...Object.fromEntries((data.columnDates || []).map(date => [date, item.dailySales?.[date] || 0]))
        }));

        return {
            tableData,
            columns: [
                { key: 'name', label: 'Name', format: 'text' },
                { key: 'area', label: 'Area', format: 'text' },
                { key: 'total', label: 'Total', format: 'currency' },
                ...(data.columnDates || []).map(date => ({ key: date, label: date, format: 'currency' }))
            ]
        };
    };

    const handleExportPDF = () => {
        try {
            const exportData = prepareExportData();
            exportToPDF({
                title: 'Weekly Sales Report',
                subtitle: 'Weekly sales breakdown by customer',
                filename: `weekly-sales-${new Date().toISOString().split('T')[0]}.pdf`,
                columns: exportData.columns,
                tableData: exportData.tableData
            });
            successToast('PDF exported successfully');
        } catch (error) {
            console.error('PDF export failed:', error);
            errorToast('Failed to export PDF');
        }
    };

    const handleExportExcel = () => {
        try {
            const exportData = prepareExportData();
            exportToExcel(exportData.tableData, {
                title: 'Weekly Sales Report',
                filename: `weekly-sales-${new Date().toISOString().split('T')[0]}.xlsx`,
                columns: exportData.columns
            });
            successToast('Excel file exported successfully');
        } catch (error) {
            console.error('Excel export failed:', error);
            errorToast('Failed to export Excel file');
        }
    };

    const handleEmailReport = () => {
        errorToast('Email functionality coming soon');
    };

    // Expose export handlers via ref
    useImperativeHandle(ref, () => ({
        handleExportPDF,
        handleExportExcel,
        handleEmailReport
    }));

    if (isLoading) {
        if (hideHeader) {
            return (
                <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">Loading sales data...</div>
                </div>
            );
        }
        return (
            <ReportLayout
                title="Weekly Sales"
                subtitle="Weekly sales breakdown"
                loading={isLoading}
                icon={FileText}
                iconColor="indigo"
            >
                <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">Loading sales data...</div>
                </div>
            </ReportLayout>
        );
    }

    if (!data || data.error || !data.data || !data.columnDates || !Array.isArray(data.data) || !Array.isArray(data.columnDates)) {
        if (hideHeader) {
            return (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                    <p className="text-gray-500">{data?.error || 'No data available'}</p>
                </div>
            );
        }
        return (
            <ReportLayout
                title="Weekly Sales"
                subtitle="Weekly sales breakdown"
                icon={FileText}
                iconColor="indigo"
                onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel}
                onEmailReport={handleEmailReport}
            >
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                    <p className="text-gray-500">{data?.error || 'No data available'}</p>
                </div>
            </ReportLayout>
        );
    }

    const content = (
        <div className="space-y-4">
            {/* Overall KPI Cards - Only show when not using hideHeader (standalone mode) */}
            {!hideHeader && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                        <div className="text-sm font-medium text-gray-600 mb-1">Total Sales</div>
                        <div className="text-2xl font-bold text-gray-900">{formatCurrency(totals?.total || 0)}</div>
                        <div className="text-xs text-gray-500 mt-1">All Customers</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
                        <div className="text-sm font-medium text-gray-600 mb-1">Customer Count</div>
                        <div className="text-2xl font-bold text-gray-900">{filteredData.length}</div>
                        <div className="text-xs text-gray-500 mt-1">Customers</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
                        <div className="text-sm font-medium text-gray-600 mb-1">Avg per Customer</div>
                        <div className="text-2xl font-bold text-gray-900">
                            {formatCurrency(filteredData.length > 0 ? (totals?.total || 0) / filteredData.length : 0)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Average</div>
                    </div>
                </div>
            )}

            {/* Sales Table */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-900">Weekly Sales Breakdown</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Area</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                {data.columnDates.map((date) => (
                                    <th
                                        key={date}
                                        className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {date}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredData.map((item, index) => {
                                // Get area color based on area name (consistent color per area)
                                const areaColors = {
                                    'North': 'bg-blue-100 text-blue-800',
                                    'South': 'bg-green-100 text-green-800',
                                    'East': 'bg-purple-100 text-purple-800',
                                    'West': 'bg-orange-100 text-orange-800',
                                    'Central': 'bg-indigo-100 text-indigo-800',
                                    'London': 'bg-red-100 text-red-800',
                                    'Midlands': 'bg-yellow-100 text-yellow-800'
                                };
                                const areaColor = areaColors[item?.area] || 'bg-gray-100 text-gray-800';
                                
                                return (
                                    <tr
                                        key={`${item?.name || 'unknown'}-${index}`}
                                        className="hover:bg-gray-50 transition-colors"
                                    >
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="text-sm font-semibold text-gray-900">{item?.name || 'N/A'}</div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {item?.area ? (
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${areaColor}`}>
                                                    {item.area}
                                                </span>
                                            ) : (
                                                <span className="text-sm text-gray-400">N/A</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="text-sm text-right font-bold text-gray-900">
                                                {formatCurrency(item?.totalValue || 0)}
                                            </div>
                                        </td>
                                        {data.columnDates.map((date) => {
                                            const value = item?.dailySales?.[date] || 0;
                                            return (
                                                <td
                                                    key={date}
                                                    className="px-4 py-3 whitespace-nowrap"
                                                >
                                                    <div className={`text-sm text-right ${
                                                        value < 0 
                                                            ? 'text-red-600 font-semibold' 
                                                            : value > 0 
                                                            ? 'text-gray-900 font-medium' 
                                                            : 'text-gray-400'
                                                    }`}>
                                                        {formatCurrency(value)}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                            {/* Totals row */}
                            {totals && (
                                <tr className="bg-gray-50 font-bold border-t-2 border-gray-300">
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="text-sm font-bold text-gray-900">Totals</div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap"></td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="text-sm text-right font-bold text-gray-900">
                                            {formatCurrency(totals.total || 0)}
                                        </div>
                                    </td>
                                    {data.columnDates.map((date) => {
                                        const value = totals.daily?.[date] || 0;
                                        return (
                                            <td
                                                key={date}
                                                className="px-4 py-3 whitespace-nowrap"
                                            >
                                                <div className={`text-sm text-right font-bold ${
                                                    value < 0 ? 'text-red-600' : 'text-gray-900'
                                                }`}>
                                                    {formatCurrency(value)}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    if (hideHeader) {
        return <div>{content}</div>;
    }

    return (
        <ReportLayout
            title="Weekly Sales"
            subtitle="Weekly sales breakdown by customer"
            icon={FileText}
            iconColor="indigo"
            onExportPDF={handleExportPDF}
            onExportExcel={handleExportExcel}
            onEmailReport={handleEmailReport}
        >
            {content}
        </ReportLayout>
    );
});

export default WeeklySales;
